import { useState, useMemo, useCallback } from 'react';

interface UsePaginationResult<T> {
  currentPage: number;
  pageSize: number;
  pageCount: number;
  paginatedData: T[];
  changePage: (newPage: number) => void;
  changePageSize: (newPageSize: number) => void;
  setData: (newData: T[]) => void;
  setDataAndResetPagination: (newData: T[]) => void;
  resetPagination: () => void;
}

const usePagination = <T>(initialPageSize = 10): UsePaginationResult<T> => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(initialPageSize);
  const [data, setDataState] = useState<T[]>([]);

  const pageCount = useMemo(() => {
    return Math.ceil(data.length / pageSize);
  }, [data.length, pageSize]);

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return data.slice(startIndex, endIndex);
  }, [currentPage, pageSize, data]);

  const changePage = useCallback((newPage: number) => {
    setCurrentPage(newPage);
  }, []);

  const changePageSize = useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page whenever page size changes
  }, []);

  const setData = useCallback((newData: T[]) => {
    setDataState(newData);
  }, []);

  const setDataAndResetPagination = useCallback((newData: T[]) => {
    setDataState(newData);
    setCurrentPage(1); // Reset to first page whenever new data is set
  }, []);

  const resetPagination = useCallback(() => {
    setCurrentPage(1);
  }, []);

  return {
    currentPage,
    pageSize,
    pageCount,
    paginatedData,
    changePage,
    changePageSize,
    setData,
    setDataAndResetPagination,
    resetPagination,
  };
};

export default usePagination;
